import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { MONTHS, computeMonthlyNeed, customProfileSum, normalizedProfile, parsePastedProfile, PROFILES } from '../calculations/monthlyNeedCalculations.js';
import MonthlyBarChart from '../MonthlyBarChart.js';
import ChartExportToolbar from '../ChartExportToolbar.js';

/**
 * Étape 1 : usages à couvrir par l'eau de pluie, volume annuel, et profil de répartition mensuelle.
 * Les lignes marquées "Importé de l'audit" sont pré-remplies à partir des usages réels du
 * bâtiment (WC, lave-linge, arrosage — cf. auditUsageImport.js) : le volume vient d'un calcul
 * réel (computeAnnualVolumeM3) mais reste librement modifiable ici, comme n'importe quelle ligne.
 * La colonne "Égout" indique si cette eau part vers l'assainissement (WC, lave-linge) ou non
 * (jardin, lavage extérieur) — utilisé à l'étape ROI pour déterminer le tarif réellement évité.
 */
const NeedsStep = ({ usages, setUsages, currentStep, totalSteps, goPrevious, goNext, canAdvance }) => {
  const { building_id } = useParams();
  const hasAuditData = usages.some(u => u.fromAudit);
  const [pasteInputs, setPasteInputs] = useState({}); // i -> { text, status: 'ok'|'bad' }
  const monthlyNeed = useMemo(() => computeMonthlyNeed(usages), [usages]);
  const totalAnnual = monthlyNeed.reduce((a, b) => a + b, 0);
  const peakIdx = monthlyNeed.indexOf(Math.max(...monthlyNeed));

  const updateUsage = (i, patch) => {
    setUsages(prev => prev.map((u, idx) => idx === i ? { ...u, ...patch } : u));
  };

  const setProfile = (i, profile) => {
    setUsages(prev => prev.map((u, idx) => {
      if (idx !== i) return u;
      if (profile === 'custom' && !u.custom) return { ...u, profile, custom: PROFILES.jardin.slice() };
      return { ...u, profile };
    }));
  };

  const setCustomValue = (i, m, value) => {
    setUsages(prev => prev.map((u, idx) => {
      if (idx !== i) return u;
      const custom = u.custom ? [...u.custom] : Array(12).fill(0);
      custom[m] = value;
      return { ...u, custom };
    }));
  };

  const applyPastedProfile = (i) => {
    const raw = (pasteInputs[i]?.text || '').trim();
    if (!raw) return;
    const nums = parsePastedProfile(raw);
    if (nums) {
      updateUsage(i, { custom: nums });
      setPasteInputs(prev => ({ ...prev, [i]: { text: '', status: 'ok' } }));
    } else {
      const count = raw.split(/[\s;]+/).filter(Boolean).length;
      setPasteInputs(prev => ({ ...prev, [i]: { text: raw, status: 'bad', count } }));
    }
  };

  const removeUsage = (i) => setUsages(prev => prev.filter((_, idx) => idx !== i));

  const addUsage = () => setUsages(prev => [...prev, { id: `custom-${Date.now()}`, name: 'Nouvel usage', volume: 1, profile: 'uniforme', custom: null, enabled: true, sewage: true, category: 'other' }]);

  const getChartDataTSV = () => {
    const rows = ['Mois\tBesoin (m³)'];
    MONTHS.forEach((m, i) => rows.push(`${m}\t${monthlyNeed[i].toFixed(3)}`));
    return rows.join('\n');
  };

  return (
    <div className="apv2-step-card">
      <div className="apv2-step-header">
        <div className="apv2-step-header-text">
          <span className="apv2-step-eyebrow">Récupération d'eau de pluie</span>
          <h2 className="apv2-step-heading">Usages à couvrir</h2>
        </div>
        <span className="apv2-step-counter">{currentStep + 1} / {totalSteps}</span>
      </div>
      <div className="apv2-step-body">
        <div className="apv2-step-explanation">
          <span className="apv2-step-explanation-icon">💡</span>
          <p className="apv2-step-explanation-text">
            {hasAuditData
              ? "Les usages compatibles avec l'eau de pluie détectés dans l'audit (WC, lave-linge, arrosage) sont pré-remplis ci-dessous avec leur volume réel. Décochez, ajustez ou ajoutez librement — ces valeurs restent une simulation, indépendante de l'audit."
              : "Aucun usage compatible (WC, lave-linge, arrosage) n'a été détecté dans l'audit de ce bâtiment — complétez la liste manuellement ci-dessous."}
            {' '}« Personnalisé » permet de saisir 12 valeurs par mois — des pourcentages (somme 100) ou de simples parts/poids
            relatifs (ex. 1, 1, 2, 3, 3, 2, 1…) : les deux fonctionnent, c'est automatiquement ramené à 100 % pour le calcul.
            La colonne « Égout » sert à l'étape 7 : coche si cette eau part vers l'assainissement (WC, lave-linge), décoche
            si elle part dehors (jardin, lavage extérieur) — ça change le montant réellement économisé.
          </p>
        </div>

        <div className="apv2-flat-table-wrapper">
          <table className="apv2-flat-table">
            <thead>
              <tr>
                <th className="apv2-flat-table-th-check" />
                <th>Usage</th>
                <th>Volume annuel</th>
                <th>Répartition</th>
                <th title="Cette eau part-elle vers l'assainissement ?">Égout</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {usages.map((u, i) => (
                <tr key={u.id}>
                  <td className="apv2-flat-table-td-check">
                    <input type="checkbox" checked={u.enabled} onChange={e => updateUsage(i, { enabled: e.target.checked })} />
                  </td>
                  <td>
                    <input
                      type="text"
                      className="rw-name-input"
                      value={u.name}
                      disabled={!u.enabled}
                      onChange={e => updateUsage(i, { name: e.target.value })}
                    />
                    {u.fromAudit && (
                      <div className="rw-audit-badge-row">
                        <span className="rw-audit-badge" title={`Volume calculé à partir de ${u.sourceCount} usage(s) réel(s) de l'audit`}>
                          Importé de l'audit ({u.sourceCount})
                        </span>
                        <a
                          className="rw-audit-link"
                          href={`/client/${building_id}/usages`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Corriger dans l'audit ↗
                        </a>
                      </div>
                    )}
                  </td>
                  <td>
                    <input
                      type="number" min="0" step="0.1"
                      className="rw-vol-input"
                      value={u.volume}
                      disabled={!u.enabled}
                      onChange={e => updateUsage(i, { volume: +e.target.value || 0 })}
                    />
                    <span className="rw-unit">m³/an</span>
                  </td>
                  <td>
                    <select
                      className="rw-profile-select"
                      value={u.profile}
                      disabled={!u.enabled}
                      onChange={e => setProfile(i, e.target.value)}
                    >
                      <option value="uniforme">Uniforme</option>
                      <option value="jardin">Jardin (pic été)</option>
                      <option value="custom">Personnalisé</option>
                    </select>
                    {u.profile === 'custom' && (
                      <>
                        <div className="rw-paste-row">
                          <input
                            type="text"
                            className="rw-paste-input"
                            placeholder="Coller 12 valeurs (depuis Excel : tabulation, espace ou ; comme séparateur)"
                            value={pasteInputs[i]?.text || ''}
                            onChange={e => setPasteInputs(prev => ({ ...prev, [i]: { text: e.target.value, status: null } }))}
                            onPaste={() => setTimeout(() => applyPastedProfile(i), 0)}
                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); applyPastedProfile(i); } }}
                            onBlur={() => applyPastedProfile(i)}
                          />
                          {pasteInputs[i]?.status && (
                            <span className={`rw-paste-status ${pasteInputs[i].status === 'ok' ? 'rw-paste-status--ok' : 'rw-paste-status--bad'}`}>
                              {pasteInputs[i].status === 'ok' ? '✓ 12 valeurs appliquées' : `${pasteInputs[i].count} valeur(s) détectée(s) — 12 attendues`}
                            </span>
                          )}
                        </div>
                        <div className="rw-custom-profile-grid">
                          {MONTHS.map((m, mi) => (
                            <div key={mi} className="rw-custom-profile-cell">
                              <label>{m}</label>
                              <input
                                type="number" min="0" step="1"
                                value={u.custom ? u.custom[mi] : 0}
                                onChange={e => setCustomValue(i, mi, +e.target.value || 0)}
                              />
                            </div>
                          ))}
                        </div>
                        {(() => {
                          const sum = customProfileSum(u);
                          return (
                            <div className={`rw-profile-sum ${sum > 0 ? 'rw-profile-sum--ok' : 'rw-profile-sum--bad'}`}>
                              Somme : {sum} (informatif — % ou parts/poids relatifs, peu importe : c'est automatiquement ramené à 100% pour le calcul)
                            </div>
                          );
                        })()}
                      </>
                    )}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={!!u.sewage}
                      disabled={!u.enabled}
                      onChange={e => updateUsage(i, { sewage: e.target.checked })}
                      title="Cette eau part-elle vers l'assainissement ?"
                    />
                  </td>
                  <td className="apv2-flat-table-td-check">
                    <button type="button" className="rw-del-btn" title="Supprimer" onClick={() => removeUsage(i)}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <button type="button" className="rw-add-btn" onClick={addUsage}>+ Ajouter un usage</button>

        <div className="apv2-summary-cards">
          <div className="apv2-summary-card">
            <span className="apv2-summary-value">{totalAnnual.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} m³</span>
            <span className="apv2-summary-label">besoin annuel total</span>
          </div>
          <div className="apv2-summary-card">
            <span className="apv2-summary-value">{totalAnnual > 0 ? `${MONTHS[peakIdx]} — ${monthlyNeed[peakIdx].toFixed(2)} m³` : '—'}</span>
            <span className="apv2-summary-label">mois le plus exigeant</span>
          </div>
        </div>

        <div className="rw-chart-block">
          <p className="rw-chart-title">Répartition mensuelle du besoin</p>
          <ChartExportToolbar containerId="rw-needs-chart" getDataTSV={getChartDataTSV} />
          <div id="rw-needs-chart">
            <MonthlyBarChart values={monthlyNeed} color="#000a43" />
          </div>
        </div>
      </div>
      <div className="apv2-step-footer">
        <button type="button" className="apv2-footer-btn-ghost" onClick={goPrevious} disabled={currentStep === 0}>
          ← Précédent
        </button>
        <button type="button" className="apv2-footer-btn-cta" onClick={goNext} disabled={!canAdvance}>
          Suivant →
        </button>
      </div>
    </div>
  );
};

export default NeedsStep;
